import type { ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';

interface CollapsibleIslandProps {
  /** Title shown in the island header. */
  title: string;
  /** Whether the island starts expanded. Defaults to `false`. */
  defaultOpen?: boolean;
  /** Body content rendered when the island is open. */
  children: ReactNode;
  /** Optional right-aligned slot in the header (badge/meta). */
  right?: ReactNode;
  /** Optional DOM id for the trigger — useful for aria associations and future persistence. */
  id?: string;
}

/**
 * Side-column "island": a collapsible card-like surface with a clickable header.
 *
 * Used in both the left ("About this artifact") and right ("Actions and
 * commands") columns of the artifact detail page so all sections share a
 * consistent visual rhythm.
 */
export function CollapsibleIsland({
  title,
  defaultOpen = false,
  children,
  right,
  id,
}: CollapsibleIslandProps): React.ReactElement {
  return (
    <Collapsible
      defaultOpen={defaultOpen}
      className="rounded-md border border-border bg-surface/60 shadow-sm"
    >
      <CollapsibleTrigger
        id={id}
        aria-label={`Toggle ${title} section`}
        className="group flex w-full items-center justify-between gap-2 px-3 py-2 text-sm font-semibold text-text-primary hover:bg-surface-elevated/40"
      >
        <span className="flex items-center gap-2">
          <ChevronDown className="size-4 text-text-secondary transition-transform group-data-[state=closed]:-rotate-90" />
          {title}
        </span>
        {right !== undefined && (
          <span className="text-xs font-normal text-text-secondary">{right}</span>
        )}
      </CollapsibleTrigger>
      <CollapsibleContent className="border-t border-border px-3 py-3">
        {children}
      </CollapsibleContent>
    </Collapsible>
  );
}
